import React, { useState, useCallback } from 'react';
import { Copy, Download, RefreshCw, Code, Settings, Cpu } from 'lucide-react';

interface CppFormatterProps {}

const CppFormatter: React.FC<CppFormatterProps> = () => {
  const [inputCode, setInputCode] = useState<string>('');
  const [formattedCode, setFormattedCode] = useState<string>('');
  const [indentSize, setIndentSize] = useState<number>(4);
  const [bracketStyle, setBracketStyle] = useState<'allman' | 'k&r' | 'stroustrup'>('allman');
  const [language, setLanguage] = useState<'c' | 'cpp'>('cpp');
  const [isFormatting, setIsFormatting] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  const cKeywords = [
    'auto', 'break', 'case', 'char', 'const', 'continue', 'default', 'do', 'double',
    'else', 'enum', 'extern', 'float', 'for', 'goto', 'if', 'int', 'long',
    'register', 'return', 'short', 'signed', 'sizeof', 'static', 'struct', 'switch',
    'typedef', 'union', 'unsigned', 'void', 'volatile', 'while'
  ];

  const cppKeywords = [
    ...cKeywords,
    'alignas', 'alignof', 'and', 'and_eq', 'asm', 'bitand', 'bitor', 'bool',
    'catch', 'class', 'compl', 'concept', 'const_cast', 'consteval', 'constexpr',
    'constinit', 'co_await', 'co_return', 'co_yield', 'decltype', 'delete',
    'dynamic_cast', 'explicit', 'export', 'false', 'friend', 'inline', 'mutable',
    'namespace', 'new', 'noexcept', 'not', 'not_eq', 'nullptr', 'operator', 'or',
    'or_eq', 'private', 'protected', 'public', 'reinterpret_cast', 'requires',
    'static_assert', 'static_cast', 'template', 'this', 'thread_local', 'throw',
    'true', 'try', 'typeid', 'typename', 'using', 'virtual', 'xor', 'xor_eq'
  ];

  const standardLibraryFunctions = [
    'printf', 'scanf', 'fprintf', 'fscanf', 'sprintf', 'sscanf', 'fopen', 'fclose',
    'fread', 'fwrite', 'fgets', 'fputs', 'malloc', 'calloc', 'realloc', 'free',
    'memcpy', 'memset', 'memcmp', 'strlen', 'strcpy', 'strcat', 'strcmp', 'strncmp',
    'atoi', 'atof', 'exit', 'abort', 'rand', 'srand', 'time', 'clock'
  ];

  const cppStandardLibrary = [
    'std::cout', 'std::cin', 'std::endl', 'std::string', 'std::vector', 'std::map',
    'std::set', 'std::list', 'std::queue', 'std::stack', 'std::pair', 'std::make_pair',
    'std::shared_ptr', 'std::unique_ptr', 'std::move', 'std::forward', 'std::swap'
  ];

  const preprocessorDirectives = [
    '#include', '#define', '#undef', '#ifdef', '#ifndef', '#if', '#else', '#elif',
    '#endif', '#error', '#pragma', '#line'
  ];

  const formatCpp = useCallback((code: string): string => {
    if (!code.trim()) return '';

    let formatted = code;
    const indent = ' '.repeat(indentSize);
    
    // Split into lines
    const lines = formatted.split('\n');
    const formattedLines: string[] = [];
    let currentIndentLevel = 0;
    let inMultilineComment = false;
    let inString = false;
    let inPreprocessor = false;

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      const trimmedLine = line.trim();
      
      // Skip empty lines but preserve them
      if (!trimmedLine) {
        formattedLines.push('');
        continue;
      }

      // Handle preprocessor directives
      if (trimmedLine.startsWith('#')) {
        inPreprocessor = true;
        formattedLines.push(trimmedLine);
        continue;
      } else {
        inPreprocessor = false;
      }

      // Handle multiline comments
      if (!inString) {
        if (trimmedLine.includes('/*') && !trimmedLine.includes('*/')) {
          inMultilineComment = true;
        } else if (trimmedLine.includes('*/') && inMultilineComment) {
          inMultilineComment = false;
        }
      }

      // Handle strings (simplified)
      if (!inMultilineComment) {
        const stringMatches = trimmedLine.match(/"/g);
        if (stringMatches) {
          const quoteCount = stringMatches.length;
          if (quoteCount % 2 === 1) {
            inString = !inString;
          }
        }
      }

      // Don't format inside multiline comments or strings
      if (inMultilineComment || inString) {
        formattedLines.push(line);
        continue;
      }

      // Handle closing braces
      if (trimmedLine.startsWith('}')) {
        currentIndentLevel = Math.max(0, currentIndentLevel - 1);
      }

      // Handle case and default labels
      if (trimmedLine.match(/^(case\s+.*:|default\s*:)/)) {
        const caseIndent = Math.max(0, currentIndentLevel - 1);
        formattedLines.push(indent.repeat(caseIndent) + trimmedLine);
        continue;
      }

      // Apply indentation
      let indentedLine = indent.repeat(currentIndentLevel) + trimmedLine;

      // Handle different bracket styles
      if (trimmedLine.includes('{')) {
        const beforeBrace = trimmedLine.substring(0, trimmedLine.indexOf('{')).trim();
        const afterBrace = trimmedLine.substring(trimmedLine.indexOf('{') + 1).trim();

        if (bracketStyle === 'allman') {
          // Allman style: braces on new line
          if (beforeBrace) {
            formattedLines.push(indent.repeat(currentIndentLevel) + beforeBrace);
          }
          formattedLines.push(indent.repeat(currentIndentLevel) + '{');
          if (afterBrace) {
            currentIndentLevel++;
            formattedLines.push(indent.repeat(currentIndentLevel) + afterBrace);
          } else {
            currentIndentLevel++;
          }
        } else if (bracketStyle === 'k&r') {
          // K&R style: opening brace same line, except for functions
          if (beforeBrace.match(/^(if|for|while|switch|catch|else)/)) {
            formattedLines.push(indent.repeat(currentIndentLevel) + beforeBrace + ' {');
            currentIndentLevel++;
            if (afterBrace) {
              formattedLines.push(indent.repeat(currentIndentLevel) + afterBrace);
            }
          } else {
            // Function or class definition
            if (beforeBrace) {
              formattedLines.push(indent.repeat(currentIndentLevel) + beforeBrace);
            }
            formattedLines.push(indent.repeat(currentIndentLevel) + '{');
            currentIndentLevel++;
            if (afterBrace) {
              formattedLines.push(indent.repeat(currentIndentLevel) + afterBrace);
            }
          }
        } else { // stroustrup
          // Stroustrup style: similar to K&R but catch/else on new line
          formattedLines.push(indent.repeat(currentIndentLevel) + beforeBrace + ' {');
          currentIndentLevel++;
          if (afterBrace) {
            formattedLines.push(indent.repeat(currentIndentLevel) + afterBrace);
          }
        }
      } else if (trimmedLine.startsWith('}')) {
        // Handle closing brace with possible else/catch
        const afterBrace = trimmedLine.substring(1).trim();
        formattedLines.push(indent.repeat(currentIndentLevel) + '}');
        
        if (afterBrace) {
          if (afterBrace.startsWith('else') || afterBrace.startsWith('catch')) {
            if (bracketStyle === 'stroustrup') {
              formattedLines.push(indent.repeat(currentIndentLevel) + afterBrace);
            } else {
              formattedLines.push(indent.repeat(currentIndentLevel) + afterBrace);
            }
          } else {
            formattedLines.push(indent.repeat(currentIndentLevel) + afterBrace);
          }
        }
      } else {
        formattedLines.push(indentedLine);
        
        // Handle indent for structures that don't have braces on same line
        if (trimmedLine.match(/^(if|for|while|switch|else|catch|try)\s*\([^)]*\)\s*$/) ||
            trimmedLine.match(/^else\s*$/) ||
            trimmedLine.match(/^(class|struct|namespace)\s+\w+\s*$/) ||
            trimmedLine.match(/^\w+.*\)\s*$/) && !trimmedLine.includes(';')) {
          // These will likely be followed by a brace
        }
      }
    }

    return formattedLines
      .map(line => line.trimEnd()) // Remove trailing spaces
      .join('\n')
      .replace(/\n{3,}/g, '\n\n'); // Limit consecutive empty lines to 2
  }, [indentSize, bracketStyle]);

  const handleFormat = useCallback(async () => {
    if (!inputCode.trim()) return;
    
    setIsFormatting(true);
    
    // Simulate formatting delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const formatted = formatCpp(inputCode);
    setFormattedCode(formatted);
    setIsFormatting(false);
  }, [inputCode, formatCpp]);

  const handleCopy = useCallback(async () => {
    if (!formattedCode) return;
    
    try {
      await navigator.clipboard.writeText(formattedCode);
      const button = document.getElementById('copy-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, [formattedCode]);

  const handleDownload = useCallback(() => {
    if (!formattedCode) return;
    
    const extension = language === 'cpp' ? 'cpp' : 'c';
    const blob = new Blob([formattedCode], { type: 'text/x-c++src' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `formatted_code.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [formattedCode, language]);

  const handleClear = useCallback(() => {
    setInputCode('');
    setFormattedCode('');
  }, []);

  const highlightCpp = useCallback((code: string): string => {
    if (!code) return '';
    
    let highlighted = code;
    
    // Escape HTML
    highlighted = highlighted
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Highlight preprocessor directives
    preprocessorDirectives.forEach(directive => {
      const regex = new RegExp(`^\\s*${directive.replace('#', '\\#')}\\b.*$`, 'gm');
      highlighted = highlighted.replace(regex, '<span class="text-orange-600 font-semibold">$&</span>');
    });
    
    // Highlight keywords based on language
    const keywords = language === 'cpp' ? cppKeywords : cKeywords;
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-purple-600 font-semibold">${keyword}</span>`);
    });
    
    // Highlight standard library functions
    standardLibraryFunctions.forEach(func => {
      const regex = new RegExp(`\\b${func}\\b(?=\\s*\\()`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-blue-600 font-semibold">${func}</span>`);
    });
    
    // Highlight C++ standard library
    if (language === 'cpp') {
      cppStandardLibrary.forEach(func => {
        const escapedFunc = func.replace(/:/g, ':');
        const regex = new RegExp(`\\b${escapedFunc}\\b`, 'g');
        highlighted = highlighted.replace(regex, `<span class="text-cyan-600 font-semibold">${func}</span>`);
      });
    }
    
    // Highlight data types
    const dataTypes = ['int', 'char', 'float', 'double', 'void', 'bool', 'long', 'short', 'unsigned', 'signed'];
    if (language === 'cpp') {
      dataTypes.push('string', 'vector', 'map', 'set', 'list', 'queue', 'stack');
    }
    dataTypes.forEach(type => {
      const regex = new RegExp(`\\b${type}\\b`, 'g');
      highlighted = highlighted.replace(regex, `<span class="text-green-600 font-semibold">${type}</span>`);
    });
    
    // Highlight strings
    highlighted = highlighted.replace(/(\"([^\"\\\\]|\\\\.)*\")/g, '<span class="text-emerald-600">$1</span>');
    highlighted = highlighted.replace(/('([^'\\\\]|\\\\.)*')/g, '<span class="text-emerald-600">$1</span>');
    
    // Highlight numbers
    highlighted = highlighted.replace(/\b\d+(\.\d+)?[fFlL]?\b/g, '<span class="text-yellow-600">$&</span>');
    highlighted = highlighted.replace(/\b0x[0-9a-fA-F]+\b/g, '<span class="text-yellow-600">$&</span>');
    
    // Highlight comments
    highlighted = highlighted.replace(/(\/\/.*$)/gm, '<span class="text-gray-500 italic">$1</span>');
    highlighted = highlighted.replace(/(\/\*[\s\S]*?\*\/)/g, '<span class="text-gray-500 italic">$1</span>');
    
    // Highlight function definitions
    highlighted = highlighted.replace(/(\w+)\s*\(/g, '<span class="text-indigo-600 font-semibold">$1</span>(');
    
    // Highlight class/struct names
    highlighted = highlighted.replace(/\b(class|struct)\s+(\w+)/g, 
      '$1 <span class="text-red-600 font-bold">$2</span>');
    
    // Highlight constants and macros
    highlighted = highlighted.replace(/\b[A-Z_][A-Z0-9_]*\b/g, '<span class="text-red-500 font-semibold">$&</span>');
    
    return highlighted;
  }, [language]);

  const exampleCode = language === 'cpp' ? `#include <iostream>
#include <vector>
#include <string>
#include <memory>
#include <algorithm>

// Forward declaration
class Shape;

// Abstract base class
class Shape {
public:
    Shape(const std::string& name) : name_(name) {}
    virtual ~Shape() = default;
    
    virtual double area() const = 0;
    virtual double perimeter() const = 0;
    virtual void draw() const {
        std::cout << "Drawing " << name_ << std::endl;
    }
    
    const std::string& getName() const { return name_; }

protected:
    std::string name_;
};

// Derived class
class Rectangle : public Shape {
private:
    double width_, height_;

public:
    Rectangle(double width, double height) 
        : Shape("Rectangle"), width_(width), height_(height) {
        if (width <= 0 || height <= 0) {
            throw std::invalid_argument("Dimensions must be positive");
        }
    }
    
    double area() const override {
        return width_ * height_;
    }
    
    double perimeter() const override {
        return 2 * (width_ + height_);
    }
    
    void draw() const override {
        Shape::draw();
        std::cout << "Width: " << width_ << ", Height: " << height_ << std::endl;
    }
};

class Circle : public Shape {
private:
    double radius_;
    static constexpr double PI = 3.14159265359;

public:
    Circle(double radius) : Shape("Circle"), radius_(radius) {
        if (radius <= 0) {
            throw std::invalid_argument("Radius must be positive");
        }
    }
    
    double area() const override {
        return PI * radius_ * radius_;
    }
    
    double perimeter() const override {
        return 2 * PI * radius_;
    }
    
    void draw() const override {
        Shape::draw();
        std::cout << "Radius: " << radius_ << std::endl;
    }
};

// Template function
template<typename T>
void printContainer(const T& container) {
    std::cout << "Container contents: ";
    for (const auto& item : container) {
        std::cout << item << " ";
    }
    std::cout << std::endl;
}

// Main function
int main() {
    try {
        // Create shapes using smart pointers
        std::vector<std::unique_ptr<Shape>> shapes;
        
        shapes.push_back(std::make_unique<Rectangle>(5.0, 3.0));
        shapes.push_back(std::make_unique<Circle>(2.5));
        shapes.push_back(std::make_unique<Rectangle>(10.0, 7.0));
        
        std::cout << "=== Shape Information ===" << std::endl;
        
        double totalArea = 0.0;
        for (const auto& shape : shapes) {
            shape->draw();
            std::cout << "Area: " << shape->area() << std::endl;
            std::cout << "Perimeter: " << shape->perimeter() << std::endl;
            std::cout << "------------------------" << std::endl;
            
            totalArea += shape->area();
        }
        
        std::cout << "Total area of all shapes: " << totalArea << std::endl;
        
        // Demonstrate template function
        std::vector<int> numbers = {1, 2, 3, 4, 5};
        printContainer(numbers);
        
        // Lambda function example
        auto isLargeShape = [](const std::unique_ptr<Shape>& shape) {
            return shape->area() > 20.0;
        };
        
        auto largeShapes = std::count_if(shapes.begin(), shapes.end(), isLargeShape);
        std::cout << "Number of large shapes (area > 20): " << largeShapes << std::endl;
        
    } catch (const std::exception& e) {
        std::cerr << "Error: " << e.what() << std::endl;
        return 1;
    }
    
    return 0;
}` : `#include <stdio.h>
#include <stdlib.h>
#include <string.h>
#include <math.h>

#define MAX_STUDENTS 100
#define MAX_NAME_LENGTH 50

// Structure definition
typedef struct {
    int id;
    char name[MAX_NAME_LENGTH];
    float grades[3];
    float average;
} Student;

// Function prototypes
void printStudent(const Student* student);
float calculateAverage(const float grades[], int count);
int compareStudents(const void* a, const void* b);
void printAllStudents(const Student students[], int count);

// Global variable
static int studentCount = 0;

// Function to calculate average grade
float calculateAverage(const float grades[], int count) {
    if (count <= 0) {
        return 0.0f;
    }
    
    float sum = 0.0f;
    for (int i = 0; i < count; i++) {
        sum += grades[i];
    }
    
    return sum / count;
}

// Function to print student information
void printStudent(const Student* student) {
    if (student == NULL) {
        printf("Error: NULL student pointer\\n");
        return;
    }
    
    printf("ID: %d\\n", student->id);
    printf("Name: %s\\n", student->name);
    printf("Grades: ");
    
    for (int i = 0; i < 3; i++) {
        printf("%.2f ", student->grades[i]);
    }
    
    printf("\\nAverage: %.2f\\n", student->average);
    printf("------------------------\\n");
}

// Comparison function for qsort
int compareStudents(const void* a, const void* b) {
    const Student* studentA = (const Student*)a;
    const Student* studentB = (const Student*)b;
    
    if (studentA->average > studentB->average) return -1;
    if (studentA->average < studentB->average) return 1;
    return 0;
}

// Function to print all students
void printAllStudents(const Student students[], int count) {
    printf("=== Student Records ===\\n");
    
    for (int i = 0; i < count; i++) {
        printf("Rank %d:\\n", i + 1);
        printStudent(&students[i]);
    }
}

// Function to add a new student
int addStudent(Student students[], const char* name, const float grades[]) {
    if (studentCount >= MAX_STUDENTS) {
        printf("Error: Maximum number of students reached\\n");
        return 0;
    }
    
    if (name == NULL || grades == NULL) {
        printf("Error: Invalid input parameters\\n");
        return 0;
    }
    
    Student* newStudent = &students[studentCount];
    newStudent->id = studentCount + 1;
    
    // Copy name safely
    strncpy(newStudent->name, name, MAX_NAME_LENGTH - 1);
    newStudent->name[MAX_NAME_LENGTH - 1] = '\\0';
    
    // Copy grades
    for (int i = 0; i < 3; i++) {
        newStudent->grades[i] = grades[i];
    }
    
    // Calculate average
    newStudent->average = calculateAverage(grades, 3);
    
    studentCount++;
    return 1;
}

// Main function
int main(void) {
    Student students[MAX_STUDENTS];
    
    // Sample data
    const char* names[] = {
        "Alice Johnson",
        "Bob Smith", 
        "Charlie Brown",
        "Diana Prince",
        "Edward Norton"
    };
    
    const float sampleGrades[][3] = {
        {85.5f, 92.0f, 78.5f},
        {90.0f, 88.5f, 93.0f},
        {76.0f, 82.5f, 79.0f},
        {95.5f, 97.0f, 94.5f},
        {83.0f, 85.5f, 87.0f}
    };
    
    // Add students
    printf("Adding students...\\n");
    for (int i = 0; i < 5; i++) {
        if (addStudent(students, names[i], sampleGrades[i])) {
            printf("Added: %s\\n", names[i]);
        } else {
            printf("Failed to add: %s\\n", names[i]);
        }
    }
    
    printf("\\n");
    
    // Sort students by average grade (descending)
    qsort(students, studentCount, sizeof(Student), compareStudents);
    
    // Print all students
    printAllStudents(students, studentCount);
    
    // Find top student
    if (studentCount > 0) {
        printf("=== Top Student ===\\n");
        printStudent(&students[0]);
    }
    
    // Calculate class statistics
    if (studentCount > 0) {
        float classTotal = 0.0f;
        float highest = students[0].average;
        float lowest = students[0].average;
        
        for (int i = 0; i < studentCount; i++) {
            classTotal += students[i].average;
            if (students[i].average > highest) {
                highest = students[i].average;
            }
            if (students[i].average < lowest) {
                lowest = students[i].average;
            }
        }
        
        printf("=== Class Statistics ===\\n");
        printf("Number of students: %d\\n", studentCount);
        printf("Class average: %.2f\\n", classTotal / studentCount);
        printf("Highest average: %.2f\\n", highest);
        printf("Lowest average: %.2f\\n", lowest);
    }
    
    return 0;
}`;

  const countLines = (text: string): number => {
    return text ? text.split('\n').length : 0;
  };

  const countWords = (text: string): number => {
    return text ? text.trim().split(/\s+/).filter(word => word.length > 0).length : 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Cpu className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">C/C++ Code Formatter</h1>
                  <p className="text-red-100">Format and beautify your C/C++ code</p>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="language" className="text-sm font-medium text-gray-700">
                    Language:
                  </label>
                  <select
                    id="language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as 'c' | 'cpp')}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="c">C</option>
                    <option value="cpp">C++</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label htmlFor="indent-size" className="text-sm font-medium text-gray-700">
                    Indent Size:
                  </label>
                  <select
                    id="indent-size"
                    value={indentSize}
                    onChange={(e) => setIndentSize(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value={2}>2 spaces</option>
                    <option value={4}>4 spaces</option>
                    <option value={8}>8 spaces</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label htmlFor="bracket-style" className="text-sm font-medium text-gray-700">
                    Bracket Style:
                  </label>
                  <select
                    id="bracket-style"
                    value={bracketStyle}
                    onChange={(e) => setBracketStyle(e.target.value as 'allman' | 'k&r' | 'stroustrup')}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="allman">Allman</option>
                    <option value="k&r">K&R</option>
                    <option value="stroustrup">Stroustrup</option>
                  </select>
                </div>
                
                <button
                  onClick={() => setInputCode(exampleCode)}
                  className="px-4 py-2 text-sm bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 rounded-md transition-colors duration-200"
                >
                  Load Example
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 h-96 lg:h-[600px]">
            {/* Input Panel */}
            <div className="flex flex-col border-r border-gray-200">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Input {language.toUpperCase()} Code</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleClear}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors duration-200"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleFormat}
                    disabled={isFormatting || !inputCode.trim()}
                    className="flex items-center space-x-1 px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white text-sm rounded transition-colors duration-200"
                  >
                    {isFormatting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Cpu className="w-4 h-4" />
                    )}
                    <span>{isFormatting ? 'Formatting...' : 'Format'}</span>
                  </button>
                </div>
              </div>
              <textarea
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder={`Paste your ${language.toUpperCase()} code here...`}
                className="flex-1 p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-inset"
                spellCheck={false}
              />
            </div>

            {/* Output Panel */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Formatted {language.toUpperCase()} Code</h2>
                <div className="flex space-x-2">
                  <button
                    id="copy-button"
                    onClick={handleCopy}
                    disabled={!formattedCode}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={!formattedCode}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4 bg-gray-900 overflow-auto">
                {formattedCode ? (
                  <pre
                    className="font-mono text-sm text-gray-100 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: highlightCpp(formattedCode)
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <Cpu className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Formatted {language.toUpperCase()} code will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <span>Characters: {inputCode.length}</span>
                <span>Lines: {countLines(inputCode)}</span>
                <span>Words: {countWords(inputCode)}</span>
              </div>
              <div className="text-xs">
                Supports {language === 'cpp' ? 'C++11/14/17/20' : 'C89/C99/C11'} standards • Multiple bracket styles • Cross-platform compatible
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CppFormatter;